import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  SidebarComponent,
  BreadcrumbComponent,
  CommandPaletteComponent,
  type SidebarItem,
  type BreadcrumbItem,
  type CommandItem,
} from '@shared/design-system';
import { IconComponent } from '@shared/icons/icon.component';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/services/auth.service';
import { ThemeService } from '../theme/theme.service';
import { BreadcrumbContextService } from '../navigation/breadcrumb-context.service';
import { CompositionNavContextService } from '../navigation/composition-nav-context.service';

const UUID_SEGMENT = /^[0-9a-f-]{36}$/i;

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    BreadcrumbComponent,
    CommandPaletteComponent,
    IconComponent,
  ],
  template: `
    <mg3p-sidebar
      [items]="sidebarItems()"
      [bottomItems]="sidebarBottomItems()"
      [collapsed]="sidebarCollapsed()"
      [showFooter]="true"
      title="MG3P"
      (collapseToggle)="sidebarCollapsed.set($event)"
    >
      <div footer>
        <div class="sidebar-footer">
          <div class="sidebar-footer__user">
            <span class="sidebar-footer__avatar" aria-hidden="true">
              <mg3p-icon name="user" size="md" />
            </span>
            <div class="sidebar-footer__info">
              <div class="sidebar-footer__name">{{ username() }}</div>
              <div class="sidebar-footer__role">Estado-Maior</div>
            </div>
          </div>
        </div>
      </div>
    </mg3p-sidebar>

    <div class="main-content" [class.main-content--collapsed]="sidebarCollapsed()">
      <header class="main-toolbar">
        <div class="main-toolbar__brand">
          <span class="main-toolbar__eyebrow">Portal institucional</span>
          <span class="main-toolbar__tagline">Portaria PMDF nº 990/2015</span>
        </div>
        <div class="main-toolbar__actions">
          <span class="main-toolbar__env">{{ environmentLabel() }}</span>
          <button type="button" class="main-toolbar__logout" (click)="logout()">Sair</button>
        </div>
      </header>

      <header class="main-header">
        <mg3p-breadcrumb
          [items]="breadcrumbs()"
          [showHome]="true"
          homeRoute="/inicio"
          homeLabel="Início"
          [showHomeIcon]="true"
        />
        <div class="main-header__actions">
          <button
            type="button"
            class="header-action"
            (click)="theme.cycle()"
            [title]="'Tema: ' + theme.modeLabel() + ' (clique para alternar)'"
            [attr.aria-label]="'Tema ' + theme.modeLabel() + ', alternar'"
          >
            @switch (theme.mode()) {
              @case ('light') {
                <mg3p-icon name="sun" size="md" />
              }
              @case ('dark') {
                <mg3p-icon name="moon" size="md" />
              }
              @default {
                <mg3p-icon name="monitor" size="md" />
              }
            }
          </button>
          <button
            type="button"
            class="header-action"
            (click)="openCommandPalette()"
            title="Busca rápida (Ctrl+K)"
            aria-label="Busca rápida"
          >
            <mg3p-icon name="search" size="md" />
          </button>
        </div>
      </header>

      <main class="main-body">
        <router-outlet />
      </main>
    </div>

    <mg3p-command-palette
      [commands]="commands"
      [open]="commandPaletteOpen()"
      (openChange)="commandPaletteOpen.set($event)"
    />
  `,
  styles: [`
    :host {
      display: flex;
      height: 100vh;
      overflow: hidden;
      font-family: var(--font-pmdf-sans);
    }

    .main-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      margin-left: 280px;
      transition: margin-left var(--transition-normal);
      overflow: hidden;
      background: var(--color-background);
    }

    .main-content--collapsed {
      margin-left: 64px;
    }

    .main-toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-md);
      padding: var(--spacing-sm) var(--spacing-lg);
      background: var(--shell-chrome-bg);
      color: var(--color-pmdf-primary-foreground);
      border-bottom: 1px solid var(--shell-chrome-border);
    }

    .main-toolbar__brand {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }

    .main-toolbar__eyebrow {
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-semibold);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      opacity: 0.9;
    }

    .main-toolbar__tagline {
      font-size: var(--font-size-sm);
      opacity: 0.85;
    }

    .main-toolbar__actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
    }

    .main-toolbar__env {
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-semibold);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 0.35rem 0.75rem;
      border-radius: var(--radius-full);
      border: 1px solid color-mix(in srgb, var(--color-pmdf-primary-foreground) 28%, transparent);
      background: color-mix(in srgb, var(--color-pmdf-primary-foreground) 10%, transparent);
    }

    .main-toolbar__logout {
      border: 1px solid color-mix(in srgb, var(--color-pmdf-primary-foreground) 38%, transparent);
      background: color-mix(in srgb, var(--color-pmdf-primary-foreground) 16%, transparent);
      color: var(--color-pmdf-primary-foreground);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      border-radius: var(--radius-full);
      padding: 0.4rem 0.85rem;
      cursor: pointer;
    }

    .main-toolbar__logout:hover {
      background: color-mix(in srgb, var(--color-pmdf-primary-foreground) 24%, transparent);
    }

    .main-toolbar__logout:focus-visible,
    .header-action:focus-visible {
      outline: 2px solid var(--color-pmdf-primary-muted);
      outline-offset: 2px;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-pmdf-primary-soft) 70%, transparent);
    }

    .main-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-md) var(--spacing-lg);
      background: var(--color-surface);
      border-bottom: 1px solid var(--color-border);
      min-height: 56px;
    }

    .main-header__actions {
      display: flex;
      gap: var(--spacing-sm);
    }

    .header-action {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      padding: 0;
      background: var(--color-pmdf-bg-elevated);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      color: var(--color-pmdf-primary);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .header-action:hover {
      background: var(--color-pmdf-primary-soft);
      border-color: var(--color-pmdf-primary-muted);
    }

    .main-body {
      flex: 1;
      overflow-y: auto;
      padding: var(--spacing-lg) var(--spacing-xl);
      background: var(--color-background);
    }

    .main-body > * {
      max-width: 1280px;
      margin-inline: auto;
      width: 100%;
    }

    .sidebar-footer {
      padding: var(--spacing-md);
    }

    .sidebar-footer__user {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm);
      background: var(--sidebar-hover-bg);
      border-radius: var(--radius-md);
    }

    .sidebar-footer__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: var(--radius-full);
      background: var(--sidebar-hover-bg);
    }

    .sidebar-footer__info {
      flex: 1;
      min-width: 0;
    }

    .sidebar-footer__name {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--sidebar-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sidebar-footer__role {
      font-size: var(--font-size-xs);
      color: var(--sidebar-text-muted);
    }

    @media (max-width: 768px) {
      .main-content,
      .main-content--collapsed {
        margin-left: 0;
      }
    }
  `],
})
export class MainLayoutComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly breadcrumbContext = inject(BreadcrumbContextService);
  private readonly compositionNav = inject(CompositionNavContextService);
  readonly theme = inject(ThemeService);

  sidebarCollapsed = signal(false);
  commandPaletteOpen = signal(false);
  breadcrumbs = signal<BreadcrumbItem[]>([]);
  username = signal('Usuário');
  environmentLabel = signal(environment.environmentName);

  private readonly comissoesSidebarItem: SidebarItem = {
    id: 'comissoes',
    label: 'Comissões',
    icon: 'layers',
    children: [
      { id: 'comissoes-painel', label: 'Painel', icon: 'home', route: '/inicio/comissoes', exact: true },
      { id: 'comissoes-lista', label: 'Lista completa', icon: 'demands', route: '/inicio/comissoes/lista' },
      { id: 'comissoes-documentos', label: 'Documentos', icon: 'demands', route: '/inicio/comissoes/documentos' },
      { id: 'comissoes-processos', label: 'Processos', icon: 'folder', route: '/inicio/comissoes/processos' },
      {
        id: 'comissoes-acompanhamento',
        label: 'Acompanhamento',
        icon: 'chart-bar',
        route: '/inicio/comissoes/acompanhamento',
      },
      { id: 'comissoes-relatorios', label: 'Relatórios', icon: 'chart-bar', route: '/inicio/comissoes/relatorios' },
    ],
  };

  readonly sidebarItems = computed((): SidebarItem[] => {
    const items: SidebarItem[] = [
      { id: 'inicio', label: 'Início', icon: 'home', route: '/inicio' },
      { id: 'demandas', label: 'Demandas', icon: 'demands', route: '/inicio/demandas' },
      { id: 'projetos', label: 'Projetos', icon: 'projects', route: '/inicio/projetos' },
      { id: 'programas', label: 'Programas', icon: 'programs', route: '/inicio/programas' },
      {
        id: 'portfolio',
        label: 'Portfólio',
        icon: 'portfolio',
        children: [
          { id: 'portfolio-view', label: 'Visão geral', icon: 'layers', route: '/inicio/portfolio' },
          { id: 'portfolio-cronograma', label: 'Cronograma', icon: 'chart-bar', route: '/inicio/portfolio/cronograma' },
          { id: 'subportfolio', label: 'Subportfólios', icon: 'folder', route: '/inicio/subportfolio' },
        ],
      },
      { id: 'auditoria', label: 'Auditoria', icon: 'monitor', route: '/inicio/auditoria' },
    ];
    if (this.authService.hasAnyRole(['ROLE_COMISSOES_VISUALIZAR'])) {
      items.push(this.comissoesSidebarItem);
    }
    return items;
  });

  readonly sidebarBottomItems = computed((): SidebarItem[] => []);

  readonly commands: CommandItem[] = [
    {
      id: 'nav-inicio',
      label: 'Ir para Início',
      icon: 'home',
      route: '/inicio',
      category: 'Navegação',
      keywords: ['dashboard', 'inicio', 'home'],
    },
    {
      id: 'nav-portfolio',
      label: 'Ir para Portfólio',
      icon: 'portfolio',
      route: '/inicio/portfolio',
      category: 'Navegação',
      keywords: ['portfólio', 'portfolio', 'exercício'],
    },
    {
      id: 'nav-subportfolio',
      label: 'Ir para Subportfólios',
      icon: 'folder',
      route: '/inicio/subportfolio',
      category: 'Navegação',
      keywords: ['subportfólio', 'subportfolio', 'art 7'],
    },
    {
      id: 'nav-programas',
      label: 'Ir para Programas',
      icon: 'programs',
      route: '/inicio/programas',
      category: 'Navegação',
    },
    {
      id: 'nav-projetos',
      label: 'Ir para Projetos',
      icon: 'projects',
      route: '/inicio/projetos',
      category: 'Navegação',
    },
    {
      id: 'nav-demandas',
      label: 'Ir para Demandas',
      icon: 'demands',
      route: '/inicio/demandas',
      category: 'Navegação',
    },
    {
      id: 'nav-auditoria',
      label: 'Ir para Auditoria',
      icon: 'monitor',
      route: '/inicio/auditoria',
      category: 'Navegação',
      keywords: ['audit', 'trilha', 'auditoria'],
    },
    {
      id: 'nav-comissoes',
      label: 'Ir para Comissões',
      icon: 'layers',
      route: '/inicio/comissoes',
      category: 'Navegação',
      keywords: ['comissões', 'comissao', 'estado-maior', 'chem'],
    },
    {
      id: 'gerir-composicao-portfolio',
      label: 'Gerir composição do portfólio',
      icon: 'layers',
      category: 'Ações',
      keywords: ['composição', 'vincular', 'portfólio', 'subportfólio'],
      action: () => this.abrirComposicaoPortfolio(),
    },
    {
      id: 'gerir-composicao-subportfolio',
      label: 'Gerir composição do subportfólio',
      icon: 'folder',
      category: 'Ações',
      keywords: ['composição', 'vincular', 'subportfólio', 'iniciativas'],
      action: () => this.abrirComposicaoSubportfolio(),
    },
    {
      id: 'new-programa',
      label: 'Novo Programa',
      icon: 'plus',
      route: '/inicio/programas/novo',
      category: 'Ações',
      keywords: ['criar', 'novo', 'programa'],
    },
    {
      id: 'new-demanda',
      label: 'Nova Demanda',
      icon: 'plus',
      route: '/inicio/demandas/novo',
      category: 'Ações',
      keywords: ['criar', 'novo', 'demanda'],
    },
    {
      id: 'new-projeto',
      label: 'Novo Projeto',
      icon: 'plus',
      route: '/inicio/projetos/novo',
      category: 'Ações',
      keywords: ['criar', 'novo', 'projeto'],
    },
    {
      id: 'new-portfolio',
      label: 'Novo Portfólio',
      icon: 'plus',
      route: '/inicio/portfolio/novo',
      category: 'Ações',
      keywords: ['criar', 'novo', 'portfolio', 'portfólio'],
    },
    {
      id: 'new-subportfolio',
      label: 'Novo Subportfólio',
      icon: 'plus',
      route: '/inicio/subportfolio/novo',
      category: 'Ações',
      keywords: ['criar', 'novo', 'subportfolio', 'subportfólio'],
    },
  ];

  constructor() {
    this.syncUser();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumbs());
    this.updateBreadcrumbs();
  }

  logout(): void {
    this.authService.logout();
  }

  openCommandPalette(): void {
    this.commandPaletteOpen.set(true);
  }

  private abrirComposicaoPortfolio(): void {
    const id = this.compositionNav.lastPortfolioId();
    if (id) {
      void this.router.navigate(['/inicio/portfolio', id, 'composicao']);
    } else {
      void this.router.navigate(['/inicio/portfolio']);
    }
  }

  private abrirComposicaoSubportfolio(): void {
    const id = this.compositionNav.lastSubportfolioId();
    if (id) {
      void this.router.navigate(['/inicio/subportfolio', id, 'composicao']);
    } else {
      void this.router.navigate(['/inicio/subportfolio']);
    }
  }

  private syncUser(): void {
    const name = this.authService.username();
    if (name) {
      this.username.set(name);
    }
  }

  private updateBreadcrumbs(): void {
    const segments = this.router.url.split('?')[0].split('/').filter((s) => s);
    const items: BreadcrumbItem[] = [];
    let currentPath = '';

    for (let index = 0; index < segments.length; index++) {
      const segment = segments[index];
      currentPath += `/${segment}`;

      if (segment === 'inicio') {
        continue;
      }

      if (UUID_SEGMENT.test(segment)) {
        const label = this.breadcrumbContext.getLabel(segment);
        if (!label) {
          continue;
        }
        const parent = segments[index - 1];
        const isLast = index === segments.length - 1;
        if (isLast) {
          items.push({ label });
        } else if (parent === 'portfolio') {
          items.push({
            label,
            route: '/inicio/portfolio',
            queryParams: { portfolioId: segment },
          });
        } else if (parent === 'subportfolio') {
          items.push({
            label,
            route: '/inicio/subportfolio',
            queryParams: { subportfolioId: segment },
          });
        } else {
          items.push({ label });
        }
        continue;
      }

      const label = this.getSegmentLabel(segment);
      const isLast = index === segments.length - 1;

      if (isLast) {
        items.push({ label });
      } else {
        items.push({ label, route: currentPath });
      }
    }

    this.breadcrumbs.set(items);
  }

  private getSegmentLabel(segment: string): string {
    const labels: Record<string, string> = {
      inicio: 'Início',
      portfolio: 'Portfólio',
      composicao: 'Composição',
      programas: 'Programas',
      projetos: 'Projetos',
      subportfolio: 'Subportfólios',
      demandas: 'Demandas',
      novo: 'Novo',
    };
    return labels[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1);
  }
}
